import { Product } from '../types/product'
import { CubeIcon, NoSymbolIcon, PlusIcon } from '@heroicons/react/24/outline'
import { bandSuffix } from '../lib/availability'

interface ProductGridProps {
  products: Product[]
  loading: boolean
  onProductSelect: (product: Product) => void
}

const ProductGrid = ({ products, loading, onProductSelect }: ProductGridProps) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
            <div className="h-32 bg-gray-200 rounded-md mb-4"></div>
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
          </div>
        ))}
      </div>
    )
  }

  if (products.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <CubeIcon className="h-12 w-12" />
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No products found</h3>
          <p className="mt-1 text-sm text-gray-500">
            No products available in this category
          </p>
        </div>
      </div>
    )
  }

  const getMinPrice = (product: Product) => {
    if (!product.variants || product.variants.length === 0) return 0
    return Math.min(...product.variants.map(v => v.price_cents))
  }

  const formatPrice = (priceCents: number) => {
    return `€${(priceCents / 100).toFixed(2)}`
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {products.map((product) => {
        const minPrice = getMinPrice(product)
        const hasImage = product.image_url && product.image_url.trim() !== ''
        // Still tappable: the register explains and points at the Availability page.
        const soldOut = !!product.sold_out

        return (
          <button
            key={product.id}
            onClick={() => onProductSelect(product)}
            className={`group bg-white rounded-lg border border-gray-200 p-4 transition-all duration-200 text-left ${
              soldOut ? 'opacity-75' : 'hover:border-primary-300 hover:shadow-md'
            }`}
          >
            {/* Product Image */}
            <div className="relative mb-4">
              {hasImage ? (
                <img
                  src={product.image_url}
                  alt={product.name}
                  className={`w-full h-32 object-cover rounded-md ${soldOut ? 'grayscale' : ''}`}
                />
              ) : (
                <div className="w-full h-32 bg-gray-100 rounded-md flex items-center justify-center">
                  <CubeIcon className="h-12 w-12 text-gray-400" />
                </div>
              )}

              {soldOut ? (
                <div className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 rounded-b-md bg-gray-800 px-2.5 py-2 text-[13px] font-semibold text-white">
                  <NoSymbolIcon className="h-4 w-4" />
                  <span>SOLD OUT</span>
                  <span className="font-medium opacity-80">{bandSuffix(product)}</span>
                </div>
              ) : (
                /* Add Button Overlay */
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-md flex items-center justify-center">
                  <div className="bg-primary-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <PlusIcon className="h-5 w-5" />
                  </div>
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-2">
              <h3 className={`font-medium text-gray-900 transition-colors ${soldOut ? '' : 'group-hover:text-primary-600'}`}>
                {product.name}
              </h3>
              
                             {product.description && (
                 <p className="text-sm text-gray-600 overflow-hidden text-ellipsis" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                   {product.description}
                 </p>
               )}

              {/* Price */}
              <div className="flex items-center justify-between">
                <span className={`text-lg font-semibold ${soldOut ? 'text-gray-400' : 'text-primary-600'}`}>
                  {minPrice > 0 ? formatPrice(minPrice) : 'N/A'}
                </span>
                
                {/* Product Type Badge */}
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  product.kind === 'BASE' 
                    ? 'bg-green-100 text-green-800'
                    : product.kind === 'MODIFIER'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-purple-100 text-purple-800'
                }`}>
                  {product.kind}
                </span>
              </div>

              {/* Variants Info */}
              {product.variants && product.variants.length > 1 && (
                <p className="text-xs text-gray-500">
                  {product.variants.length} variants available
                </p>
              )}
            </div>
          </button>
        )
      })}
    </div>
  )
}

export default ProductGrid
